export const defaultAuthRedirectTarget = '/';

const internalOrigin = 'https://workflowxm.local';
const invalidPostLoginPathnames = new Set(['/sign-in']);
const RETURN_TARGET_STORAGE_KEY = 'wxm.return-target';

interface RedirectLocationLike {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}

export function normalizeAuthRedirectTarget(redirectTarget?: string | null) {
  if (!redirectTarget?.trim()) {
    return defaultAuthRedirectTarget;
  }

  const trimmedRedirectTarget = redirectTarget.trim();

  if (trimmedRedirectTarget.startsWith('http://') || trimmedRedirectTarget.startsWith('https://')) {
    try {
      const parsedAbsoluteRedirectTarget = new URL(trimmedRedirectTarget);

      if (parsedAbsoluteRedirectTarget.origin !== internalOrigin) {
        return defaultAuthRedirectTarget;
      }

      if (invalidPostLoginPathnames.has(parsedAbsoluteRedirectTarget.pathname)) {
        return defaultAuthRedirectTarget;
      }

      return `${parsedAbsoluteRedirectTarget.pathname}${parsedAbsoluteRedirectTarget.search}${parsedAbsoluteRedirectTarget.hash}` || defaultAuthRedirectTarget;
    } catch {
      return defaultAuthRedirectTarget;
    }
  }

  if (!trimmedRedirectTarget.startsWith('/') || trimmedRedirectTarget.startsWith('//') || trimmedRedirectTarget.startsWith('/\\')) {
    return defaultAuthRedirectTarget;
  }

  if (/\r|\n/.test(trimmedRedirectTarget)) {
    return defaultAuthRedirectTarget;
  }

  try {
    const parsedRedirectTarget = new URL(trimmedRedirectTarget, internalOrigin);

    if (parsedRedirectTarget.origin !== internalOrigin) {
      return defaultAuthRedirectTarget;
    }

    if (invalidPostLoginPathnames.has(parsedRedirectTarget.pathname)) {
      return defaultAuthRedirectTarget;
    }

    return `${parsedRedirectTarget.pathname}${parsedRedirectTarget.search}${parsedRedirectTarget.hash}` || defaultAuthRedirectTarget;
  } catch {
    return defaultAuthRedirectTarget;
  }
}

export function buildAuthRedirectTarget(location?: RedirectLocationLike) {
  if (typeof location?.pathname !== 'string') {
    return defaultAuthRedirectTarget;
  }

  const search = typeof location.search === 'string' ? location.search : '';
  const hash = typeof location.hash === 'string' ? location.hash : '';

  return normalizeAuthRedirectTarget(`${location.pathname}${search}${hash}`);
}

export function storePostAuthRedirectTarget(redirectTarget?: string | null) {
  const normalizedRedirectTarget = normalizeAuthRedirectTarget(redirectTarget);

  try {
    if (normalizedRedirectTarget === defaultAuthRedirectTarget) {
      window.sessionStorage.removeItem(RETURN_TARGET_STORAGE_KEY);

      return;
    }

    // we use sessionStorage here because we want the redirect target to be cleared when the browser tab is closed, and we don't want it to persist across tabs, windows, or sessions.
    window.sessionStorage.setItem(RETURN_TARGET_STORAGE_KEY, normalizedRedirectTarget);
  } catch {
    // sessionStorage can be unavailable; the redirect simply falls back to the default target.
  }
}

export function consumePostAuthRedirectTarget() {
  try {
    const storedRedirectTarget = window.sessionStorage.getItem(RETURN_TARGET_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_TARGET_STORAGE_KEY);

    return normalizeAuthRedirectTarget(storedRedirectTarget);
  } catch {
    return defaultAuthRedirectTarget;
  }
}
